import {Stack} from 'src/design/atoms/Stack';
import React, {useMemo, useState} from 'react';
import type {CourseDefinitionContentResponse} from 'src/api/ProactApiClient';
import type {CourseDefinitionContentTypeEnum} from 'src/api/ProactApiClient';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {CreateDefaultCourseDefinitionContent} from 'src/app/training/courseDefaults';
import {validateCourseDefinitionContentsDetailed} from 'src/app/training/courseContentValidation';
import {
  filterContentsByLanguage,
  getAvailableLanguages,
  languageNames,
} from 'src/app/training/languageUtils';
import {LanguageManager} from 'src/pages/Modules/Training/shared/editCourseDefinition/LanguageManager';
import {ContentEditorPanel} from './ContentEditorPanel';
import {contentSelectionKey, CourseOutline} from './CourseOutline';
import {CourseSettingsSection} from './CourseSettingsSection';
import {LivePreviewPane} from './LivePreviewPane';
import {ValidationPanel} from './ValidationPanel';
import classes from './BuildTab.module.css';

export interface BuildTabProps {
  contents: CourseDefinitionContentResponse[];
  defaultLanguage: string;
  readOnly?: boolean;
  onContentsChange: (contents: CourseDefinitionContentResponse[]) => void;
}

/**
 * Course Workspace's Build tab: course settings as a collapsible header
 * section, then a two-part canvas — the content outline (Column 1) and a
 * single mounted editor for the selected item (Column 2) — plus a live
 * read-only preview (Column 3), with the validation panel atop the outline.
 *
 * Selection is parent-owned here so add/reorder/delete can enforce the
 * outline's selection-stability rules. Quiz questions are not selectable
 * outline nodes; they render inline inside the quiz's editor.
 */
export const BuildTab: React.FC<BuildTabProps> = ({
  contents,
  defaultLanguage,
  readOnly,
  onContentsChange,
}) => {
  const availableLanguages = useMemo(
    () => getAvailableLanguages(contents, defaultLanguage),
    [contents, defaultLanguage]
  );
  const [activeLanguage, setActiveLanguage] = useState(defaultLanguage);
  const filteredContents = useMemo(
    () => filterContentsByLanguage(contents, activeLanguage),
    [contents, activeLanguage]
  );

  const unusedLanguages = useMemo(() => {
    const used = new Set(availableLanguages);
    return Object.entries(languageNames)
      .filter(([code]) => !used.has(code))
      .map(([code, name]) => ({value: code, label: name}));
  }, [availableLanguages]);

  const handleAddLanguage = (code: string) => {
    const newContent = CreateDefaultCourseDefinitionContent();
    newContent.language = code;
    onContentsChange([...contents, newContent]);
    setActiveLanguage(code);
  };

  const handleRemoveLanguage = (code: string) => {
    if (code === defaultLanguage) return;
    onContentsChange(contents.filter(content => content.language !== code));
    setActiveLanguage(defaultLanguage);
  };

  const [selectedKey, setSelectedKey] = useState<string | undefined>(undefined);
  const [pendingDeleteInvariantId, setPendingDeleteInvariantId] = useState<
    string | undefined
  >(undefined);

  const offenders = useMemo(
    () => validateCourseDefinitionContentsDetailed(filteredContents),
    [filteredContents]
  );
  const invalidInvariantIds = useMemo(
    () => new Set(offenders.map(o => o.invariantId)),
    [offenders]
  );

  const updateContentInPlace = (
    invariantId: string,
    updater: (
      content: CourseDefinitionContentResponse
    ) => CourseDefinitionContentResponse
  ) => {
    onContentsChange(
      contents.map(content =>
        content.invariantId === invariantId ? updater(content) : content
      )
    );
  };

  const handleAddContent = (type: CourseDefinitionContentTypeEnum) => {
    const newContent = CreateDefaultCourseDefinitionContent();
    newContent.language = activeLanguage;
    newContent.type = type;
    onContentsChange([...contents, newContent]);
    setSelectedKey(contentSelectionKey(newContent));
  };

  const handleDeleteContentRequest = (invariantId: string) => {
    setPendingDeleteInvariantId(invariantId);
  };

  const handleDeleteContentConfirm = () => {
    if (!pendingDeleteInvariantId) return;
    const remaining = contents.filter(
      content => content.invariantId !== pendingDeleteInvariantId
    );
    onContentsChange(remaining);
    if (selectedKey === pendingDeleteInvariantId) {
      setSelectedKey(undefined);
    }
    setPendingDeleteInvariantId(undefined);
  };

  const handleReorderContents = (
    reorderedVisible: CourseDefinitionContentResponse[]
  ) => {
    // Splice the reordered (language-filtered) list back into the full
    // contents array, preserving other languages' positions untouched.
    const otherLanguage = contents.filter(
      content => content.language !== activeLanguage
    );
    onContentsChange([...otherLanguage, ...reorderedVisible]);
  };

  const selectedContent = filteredContents.find(
    content => contentSelectionKey(content) === selectedKey
  );
  const selectedContentIndex = filteredContents.findIndex(
    content => contentSelectionKey(content) === selectedKey
  );

  const languageCard = (availableLanguages.length > 1 ||
    unusedLanguages.length > 0) && (
    <SectionCard title="Languages" h="100%">
      <div style={{padding: 'var(--mantine-spacing-md)'}}>
        <LanguageManager
          availableLanguages={availableLanguages}
          activeLanguage={activeLanguage}
          defaultLanguage={defaultLanguage}
          unusedLanguages={unusedLanguages}
          onActiveLanguageChange={setActiveLanguage}
          onAddLanguage={handleAddLanguage}
          onRemoveLanguage={handleRemoveLanguage}
        />
      </div>
    </SectionCard>
  );

  return (
    <Stack gap="md">
      <CourseSettingsSection
        disabled={readOnly}
        extraCard={languageCard || undefined}
      />

      <div className={classes.canvas}>
        <SectionCard title="Outline" className={classes.pane}>
          <Stack gap="md" className={classes.paneBody}>
            <ValidationPanel
              offenders={offenders}
              onSelectOffender={invariantId => setSelectedKey(invariantId)}
            />
            <CourseOutline
              contents={filteredContents}
              invalidInvariantIds={invalidInvariantIds}
              selectedKey={selectedKey}
              disabled={readOnly}
              onSelect={setSelectedKey}
              onAddContent={handleAddContent}
              onDeleteContent={handleDeleteContentRequest}
              onReorderContents={handleReorderContents}
            />
          </Stack>
        </SectionCard>

        <SectionCard title="Editor" className={classes.pane}>
          {/* Keyed on selection so the scroll container remounts at scroll top. */}
          <div key={selectedKey} className={classes.paneBody}>
            {selectedContent ? (
              <ContentEditorPanel
                content={selectedContent}
                disabled={readOnly}
                onContentChange={updated =>
                  updateContentInPlace(
                    selectedContent.invariantId!,
                    () => updated
                  )
                }
                onDelete={() =>
                  handleDeleteContentRequest(selectedContent.invariantId!)
                }
              />
            ) : (
              <span>Select a content item from the outline to edit it.</span>
            )}
          </div>
        </SectionCard>

        <SectionCard title="Live preview" className={classes.pane}>
          <div key={selectedKey} className={classes.paneBody}>
            <LivePreviewPane
              content={selectedContent}
              index={selectedContentIndex >= 0 ? selectedContentIndex : 0}
              totalContent={filteredContents.length}
            />
          </div>
        </SectionCard>
      </div>

      <ConfirmDialog
        opened={pendingDeleteInvariantId !== undefined}
        onClose={() => setPendingDeleteInvariantId(undefined)}
        onConfirm={handleDeleteContentConfirm}
        confirmationButtonColor="red"
        title="Delete Content"
        body="Are you sure you want to delete this content item? This cannot be undone."
        buttonTextYes="Delete"
      />
    </Stack>
  );
};
